import { p as placeholders } from './app-bundle.vendor.js';
import { a as api } from './app-chunk-vendor-modules-m0hYGuxH.js';

/**
 * @typedef SelectorOrEl
 * @type {Node|NodeList|Array<HTMLElement>|string} selectorOrEl
 */

const fadeIn = (selectorOrEl, displayValue = "block") => {
  const el = getElementsFor(selectorOrEl)[0];
  el.style.opacity = 0;
  el.style.transition = "opacity ease-in-out 300ms";
  el.style.display = displayValue;

  window.setTimeout(() => {
    el.style.opacity = 1;
  }, 20);
};

const appendTextNode = (selectorOrEl, text, container = document) => {
  const el = qs(selectorOrEl, container);
  const span = document.createElement("span");
  span.textContent = text;
  el.appendChild(span);
};

/**
 * @param {string} selector
 * @param {HTMLElement|Document|undefined} container
 * @returns {NodeListOf<HTMLElement>}
 */
const qsa = (selector, container = document) =>
  container.querySelectorAll(selector);

/**
 * @param {string | HTMLElement} selectorOrEl
 * @param {HTMLElement|Document} container
 * @returns {HTMLElement|null}
 */
const qs = (selectorOrEl, container = document) =>
  typeof selectorOrEl === "string"
    ? container.querySelector(selectorOrEl)
    : selectorOrEl;

/**
 * @param {Node|NodeList|Array<HTMLElement>|string} selectorOrEl
 * @param {HTMLElement|Document|undefined} container
 * @returns {NodeList|Node[]}
 */
const getElementsFor = (selectorOrEl, container = document) => {
  if (selectorOrEl instanceof Node) {
    return [selectorOrEl]; // not the same as a node list
  }
  if (selectorOrEl instanceof NodeList) {
    return selectorOrEl;
  }

  if (selectorOrEl instanceof Array) {
    return selectorOrEl;
  }

  if (typeof selectorOrEl === "string") {
    return qsa(selectorOrEl, container);
  }

  console.log(selectorOrEl);
  throw new Error("getElementsFor expects a string, element, or node list");
};

/**
 * @param {HTMLElement} el
 * @param {string} className
 * @returns {boolean}
 */
const hasClass = (el, className) => {
  return el.classList.contains(className);
};

/**
 * @param {SelectorOrEl} selectorOrEl
 * @param {string} className
 * @returns {void}
 */
const addClass = (selectorOrEl, className) => {
  const elements = getElementsFor(selectorOrEl);
  runAll(elements, (el) => el.classList.add(className));
};

const removeClass = (selectorOrEl, className) => {
  const elements = getElementsFor(selectorOrEl);
  runAll(elements, (el) => el.classList.remove(className));
};

const change = (selectorOrEl, fn) => {
  runAll(selectorOrEl, (el) => {
    el.addEventListener("change", (e) => fn(el, e));
  });
};

/**
 * @param {string | HTMLElement} selectorOrEl
 * @param {(el:HTMLElement, e:MouseEvent) => void} fn
 */
const click = (selectorOrEl, fn) => {
  runAll(selectorOrEl, (el) => {
    el.addEventListener("click", (e) => fn(el, e));
  });
};

const getAttr = (selectorOrEl, attr, container = document) => {
  const elements = getElementsFor(selectorOrEl, container);
  return elements[0].getAttribute(attr);
};

const setDataAttr = (selectorOrEl, attr, value) => {
  const elements = getElementsFor(selectorOrEl);
  for (let i = 0; i < elements.length; i++) {
    elements[i].setAttribute("data-" + attr, value);
  }
};

/**
 * @param {HTMLElement} el
 * @param {string} selector
 * @returns {HTMLElement|null}
 */
const firstMatchingParent = (el, selector) => {
  /**
   * @param {HTMLElement|Document}
   */
  let p = el.parentNode;
  while (p && p !== document) {
    // @ts-ignore
    if (p.matches(selector)) {
      // @ts-ignore
      return p;
    }
    p = p.parentNode;
  }
  return null;
};

/**
 * @param {SelectorOrEl} selector
 * @param {(el: HTMLElement) => void} fn
 * @return {void}
 */
const runAll = (selector, fn) => {
  const elements = getElementsFor(selector);
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    // @ts-ignore
    fn(el);
  }
};

const setText = (selectorOrEl, text) => {
  const elements = getElementsFor(selectorOrEl);

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    if (el.textContent) {
      el.textContent = text;
    } else {
      el.innerHTML = text;
    }
  }
};

const setClassWhenTrue = (selectorOrEl, className, condition) => {
  const elements = getElementsFor(selectorOrEl);
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    if (condition) {
      addClass(el, className);
    } else {
      removeClass(el, className);
    }
  }
};

const getFirstMatchingInfoWithAttr = (el, attrName) => {
  while (el) {
    const value = getAttr(el, attrName);

    // Value can be empty string
    if (value !== null) {
      return [el, value];
    }
    el = el.parentNode;
  }

  throw new Error(`No ${attrName} attribute found parent nodes`);
};

const md = 768;

const pluralize = (count, singular, plural) => {
  return count === 1 ? `${count} ${singular}` : `${count} ${plural}`;
};

/**
 * @param {string} selector
 * @param {HTMLElement | document | undefined } [container=document]
 * @returns {unknown | null}
 */
const getScriptTagJson = (selector, container) => {
  const scriptTag = qs(selector, container);

  if (!scriptTag) {
    return null;
  }

  // Replace new lines sometimes added by liquid
  const text = scriptTag.innerText.replace(/\n/g, "");
  return JSON.parse(text);
};

const formatPrice = (price) => {
  return "$" + price.toFixed(2);
};

const formatShopifyPrice = (priceInCents) => {
  return "$" + (priceInCents / 100).toFixed(2);
};

const isMobile = () => {
  if (window.matchMedia(`(min-width: ${md}px)`).matches) {
    return false;
  }
  return true;
};

const triggerResizeEvent = () => {
  if (window.Event) {
    // Modern
    window.dispatchEvent(new Event("resize"));
  } else {
    // IE
    const resizeEvent = window.document.createEvent("UIEvents");
    resizeEvent.initUIEvent("resize", true, false, window, 0);
    window.dispatchEvent(resizeEvent);
  }
};

const fromTodayDuration = (dateStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  const diffTime = today.getTime() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 3600 * 24));

  if (diffDays >= 0) {
    if (diffDays === 0) {
      return "Today";
    }
    if (diffDays === 1) {
      return "1 day ago";
    }
    if (diffDays >= 30) {
      return "30+ days ago";
    }
    return diffDays + " days ago";
  }
  return "";
};

function onReady(fn) {
  if (
    document.readyState === "complete" ||
    document.readyState === "interactive"
  ) {
    setTimeout(fn, 1);
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
}

const greetingText = (name) => {
  const hours = new Date().getHours();
  let message = `Welcome, ${name}!`;

  if (hours >= 2 && hours < 12) {
    message = `Good morning, ${name}.`;
  } else if (hours >= 12 && hours <= 18) {
    message = `Good afternoon, ${name}.`;
  } else {
    message = `Good evening, ${name}.`;
  }
  return message;
};

/**
 * @param {"json"|"javascript"} type
 * @returns {RequestInit}
 */
function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/**
 * @param {string} msg
 */

/**
 * @param {'GET'|'POST'} method
 * @param {string} path
 * @param {Object|undefined|null} [body=undefined]
 * @returns {Promise<any>}
 */
async function request$1(method, path, body) {
  const res = await fetch(path, {
    method: method,
    headers: {
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  return await res.json();
}

/**
 * @returns {Promise<Cart>}
 */
async function getCart$1() {
  return await request$1("GET", "/cart?view=full.json");
}

/**
 * @param {Cart} cart
 * @param {boolean} refreshCartWhenNeeded
 * @returns {Promise<Cart>}
 */
async function removeAllGifts(cart, refreshCartWhenNeeded = true) {
  /** @type {Record<string, number>} */
  let updates = {};

  for (const item of cart.items) {
    if (item.is_free_gift) {
      updates[item.key] = 0;
    }
  }

  if (Object.keys(updates).length > 0) {
    await request$1("POST", "/cart/update.js", { updates });

    if (refreshCartWhenNeeded) {
      cart = await getCart$1();
    }
  }

  return cart;
}

/**
 * @param {number} variantId
 * @returns {Promise<void>}
 */
async function addFreeGift(variantId) {
  const body = {
    quantity: 1,
    id: variantId,
    properties: {
      _freegift: true,
    },
  };

  await request$1("POST", "/cart/add.js", body);
}

/**
 * @param {Cart} cart
 * @returns {Promise<Cart>}
 */
const applyFreeGift = async (cart) => {
  if (!cart.fg.active) {
    return await removeAllGifts(cart);
  }

  const minQuantity = cart.fg.quantity;
  const variant = Number((cart.fg.variant || "").split("/").pop());
  const products = cart.fg.products.map((x) =>
    parseInt(x.split("/").pop() || "")
  );

  // If not all the settings are configured we don't run a rule
  if (minQuantity < 2 || products.length === 0 || !variant) {
    return await removeAllGifts(cart);
  }

  let count = 0;
  let isEligible = false;

  /** @type {LineItem|null} */
  let freegiftLineItem = null;

  for (const item of cart.items) {
    // We only match the product for non-subscriptions
    if (products.includes(item.product_id) && item.selling_plan === null) {
      count += item.quantity;
    }

    if (item.is_free_gift === true) {
      freegiftLineItem = item;
    }
  }

  if (count >= minQuantity) {
    isEligible = true;
  }

  // If not eligible with a gift just remove
  if (!isEligible && freegiftLineItem) {
    return await removeAllGifts(cart);
  }

  // If not eligible with no gift in cart just return existing cart (no need to refetch)
  if (!isEligible) {
    return cart;
  }

  // Eligible, so if gift already in cart do nothing
  if (freegiftLineItem && freegiftLineItem.variant_id === variant) {
    return cart;
  }

  // Eligible, so if different gift in cart then remove it
  if (freegiftLineItem && freegiftLineItem.variant_id !== variant) {
    await removeAllGifts(cart, false);
  }
  await addFreeGift(variant);

  return await getCart$1();
};

const replaceChildWithHTML = (selectorOrEl, html) => {
  const el = typeof selectorOrEl === "string" ? qs(selectorOrEl) : selectorOrEl;
  el.innerHTML = html;
};

const generateHTMLFromTemplate = (
  data,
  containerTemplate,
  itemTemplateSelector
) => {
  const containerHTML = qs(containerTemplate).innerHTML;

  if (itemTemplateSelector && data.items && data.items.length > 0) {
    const itemTemplate = qs(itemTemplateSelector).innerHTML;
    const childHTML = [];

    for (let i = 0; i < data.items.length; i++) {
      const itemData = data.items[i];
      const itemHTML = placeholders(itemTemplate, itemData);
      childHTML.push(itemHTML);
    }
    data.CHILD_HTML = childHTML.join("\n");
  }

  const container = placeholders(containerHTML, data);
  return container;
};

const closeIcon = `
<svg area-hidden="true" focusable="false" role="presentation" class="icon-close" width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M18.3231 5.2085L12.5002 11.0314L6.67725 5.2085L5.2085 6.67725L11.0314 12.5002L5.2085 18.3231L6.67725 19.7918L12.5002 13.9689L18.3231 19.7918L19.7918 18.3231L13.9689 12.5002L19.7918 6.67725L18.3231 5.2085Z" fill="black"/>
</svg>`;

const modalTemplate = `
  <div class="dialog is-{{size}}">
    <button type="button" class="dialog__close-icon dialog__close-action">
      ${closeIcon}
      <span class="icon-fallback-text">Close</span>
    </button>
    <div class="dialog__body">{{content}}</div>
    <div class="dialog__actions">
      <button class="dialog__confirm-btn button is-solid dialog__close-action">
        {{buttonText}}
      </button>
    </div>
  </div>
`;

const modalCommonTemplate = `
  <div class="dialog is-{{size}} is-common-layout" role="dialog" aria-labelledby="dialog-title" aria-modal="true">
    <div class="dialog__header">
      <div class="dialog__title" id="dialog-title">{{title}}</div>
      <button type="button" class="dialog__close-icon dialog__close-action">
        ${closeIcon}
        <span class="icon-fallback-text">Close</span>
      </button>
    </div>
    <div class="dialog__body">{{content}}</div>
    <div class="dialog__actions">
      <button class="dialog__cancel-btn button is-link dialog__close-action">
        Cancel
      </button>
      <button class="dialog__confirm-btn button is-solid" data-action="save">
        {{buttonText}}
      </button>
    </div>
  </div>
`;

const handleKeydownEvent$1 = (e) => {
  if (e.key === "Escape" || e.key === "esc") {
    closeDialogOrModal();
  }
};

const closeDialogOrModal = () => {
  document.removeEventListener("keydown", handleKeydownEvent$1);
  const dialogOrModal = qs(".dialog");
  document.body.classList.remove("has-modal");
  window.setTimeout(() => {
    document.body.removeChild(dialogOrModal);
  }, 100);
};

const showModal = () => {
  window.setTimeout(() => {
    document.body.classList.add("has-modal");
  }, 100);
};

const showModalWithContent = (
  html,
  options,
  handler,
  onCloseHandler
) => {
  const defaults = {
    size: "default",
    layout: "default",
    buttonText: "OK",
    singleAction: false,
    modalBodyClass: "",
  };
  options = { ...defaults, ...options };
  const hasExistingDialog = !!qs(".dialog");
  const data = { ...options, content: html };
  const tmpl =
    options.layout === "common" ? modalCommonTemplate : modalTemplate;
  const modalHTML = placeholders(tmpl, data);

  if (!hasExistingDialog) {
    document.body.insertAdjacentHTML("beforeend", modalHTML);
  }

  /** @type {HTMLElement} */
  // @ts-ignore
  const modal = qs(".dialog");

  /** @type {HTMLElement} */
  // @ts-ignore
  const modalBody = modal.querySelector(".dialog__body");

  if (options.modalBodyClass) {
    modalBody.classList.add(options.modalBodyClass);
  }

  modalBody.innerHTML = html;

  if (options.singleAction === true) {
    modal.classList.add("is-single-action");
  }

  if (!hasExistingDialog) {
    document.addEventListener("keydown", handleKeydownEvent$1);

    click(".dialog__close-action", () => {
      closeDialogOrModal();
      if (onCloseHandler) {
        onCloseHandler();
      }
    });
  }

  if (!hasExistingDialog && handler) {
    modal.addEventListener("click", (e) => {
      if (e.target.matches("[data-action]")) {
        e.stopPropagation();
        const action = getAttr(e.target, "data-action");
        const id = getAttr(e.target, "data-action-id");
        closeDialogOrModal();
        handler(action, id, modal);
      }
    });
  }

  if (options.onBeforeShow) {
    options.onBeforeShow(modal);
  }

  showModal();
};

const showAlertDialog = (msg) => {
  showModalWithContent(msg, { size: "small" });
};

const showLargeModal = (
  data,
  template,
  itemTemplate,
  options,
  handler
) => {
  const html = generateHTMLFromTemplate(data, template, itemTemplate);
  showModalWithContent(html, { ...options, size: "large" }, handler);
};

/**
 * @param {string} prefix
 * @param {any} msg
 * @param {"red"|"lime"|"yellow"|"orange"} [color]
 * @param {any} [other]
 */

const log = (prefix, msg, color = "lime", ...other) => {
};

/** @typedef {Object} LineItemUpdateBody
 *  @property {number} qty
 *  @property {string} safeItemKey
 */

// Define a custom error class
class CustomShopifyAPIError extends Error {
  /** @param {string} message */
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
  }
}
/**
 *
 * We use this to create our own vesrion othe line_item.key since that currently
 * is unreliable due a platform bug/change. If an unrelated item or cart is changed
 * the keys change. An RechareSMS does this in the background so we can never be certain that it's
 * working correctly. We also assume we don't use item properties but if so we at least compare
 * the length.
 *
 * @param {import("../../types/cart").LineItem} item{LineItem} item{LineItem} item{LineItem} item
 * @returns {string}
 */

const buildSafeLineItemKey = (item) => {
  return `${item.variant_id}-${
    item.selling_plan ? item.selling_plan.id : "0"
  }-${Object.keys(item.properties).length}`;
};

/**
 * @param {string} safeItemKey
 * @returns {{variantId: number, planId: number, propertyCount: number}}
 */
const parseSafeItemKey = (safeItemKey) => {
  const arr = safeItemKey.split("-");

  if (arr.length !== 3) {
    throw new Error(
      `[ERROR] Unable to parse safeItemKey, wrong format: ${safeItemKey}`
    );
  }

  const [variantId, planId, propertyCount] = arr.map((x) => Number(x));

  return {
    variantId,
    planId,
    propertyCount,
  };
};

/**
 * @param {"GET"|"POST"} method
 * @param {string} path
 * @param {Object|undefined|null} [body=undefined]
 * @returns {Promise<Response>}
 */
const apiRequestRaw = async (method, path, body) => {

  const res = await fetch(path, {
    method: method,
    headers: {
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  log("API", `${method} ${path} => ${res.status}`, "yellow");
  return res;
};

/**
 * @param {"GET"|"POST"} method
 * @param {string} path
 * @param {Object|undefined|null} [body=undefined]
 * @returns {Promise<any>}
 */
const apiRequest = async (method, path, body) => {
  const res = await apiRequestRaw(method, path, body);
  const data = await res.json();

  if (res.status == 422 && data) {
    const text = data.description || data.message;
    if (text) {
      showAlertDialog(text);
    }
  }
  return data;
};

/**
 * @param {string} productHandle
 * @returns {Promise<any>}
 */
const getProductByHandle = async (productHandle) => {
  const url = `/products/${productHandle}.js`;
  return await apiRequest("GET", url);
};

/**
 * @param {string} path
 * @returns {Promise<string>}
 */
const getHTML = async (path) => {
  const res = await fetch(path, {
    method: "GET",
    credentials: "same-origin",
    headers: {
      "Content-Type": "text/html",
      "X-Requested-With": "XMLHttpRequest",
    },
  });
  return await res.text();
};

/**
 * @param {Object} body
 * @returns {Promise<any>}
 */
const addToCartRaw = async (body) => {
  return await apiRequest("POST", "/cart/add.js", body);
};

/**
 * @param {number} quantity
 * @param {number} variantId
 * @returns {Promise<any>}
 */
const addToCart = async (quantity, variantId) => {
  const body = {
    quantity: quantity,
    id: variantId,
  };
  return await apiRequest("POST", "/cart/add.js", body);
};

/**
 * @returns {Promise<Cart>}
 */
const getCart = async () => {
  let data = await apiRequest("GET", "/cart?view=full.json");
  log();
  data = await applyFreeGift(data);

  for (let i = 0; i < data.items.length; i++) {
    const item = data.items[i];
    const propertiesMap = {};
    if (item.properties) {
      for (let k = 0; k < item.properties.length; k++) {
        const [key, value] = item.properties[k];
        // @ts-ignore
        propertiesMap[key] = value;
      }
    }
    item.properties = propertiesMap;
  }
  return data;
};

/**
 * @param {string} safeItemKey
 * @returns {Promise<string>}
 */
const findRealCartKeyBySafeKey = async (safeItemKey, isRetry = false) => {
  const keyObj = parseSafeItemKey(safeItemKey);

  /** @type {Cart|null} */
  let cart = null;

  if (isRetry === true) {
    cart = await getCart();
    window.Shopify.cart = cart;
  } else {
    if (window.Shopify.cart) {
      cart = window.Shopify.cart;
    } else {
      cart = await getCart();
    }
  }

  if (!cart) {
    throw new Error("[ERROR CART KEY] Cart should never be null.");
  }

  const variantMatches = cart.items.filter(
    (item) => item.variant_id === keyObj.variantId
  );

  // If we only have one line item using that variant id we know it's a match so can just
  // return that. We'll only have multiple when you a subscription + one time product or different
  // line item properties. This is just here to be a bit safer.
  if (variantMatches.length === 1) {
    return variantMatches[0].key;
  }

  const item =
    cart.items.find((item) => buildSafeLineItemKey(item) === safeItemKey) ||
    null;

  if (item) {
    return item.key;
  }

  if (isRetry) {
    throw new CustomShopifyAPIError(
      `Could not find matching line item safeKey => ${safeItemKey}`
    );
  }

  // If no match we safe guard by calling again but we'll fetch the cart before any checks.
  // And this one won't be recursive.
  return findRealCartKeyBySafeKey(safeItemKey, true);
};

/**
 * @param {string} note
 * @returns {Promise<any>}
 */
const updateCartNote = async (note) => {
  return await apiRequest("POST", "/cart/update.js", {
    note: note,
  });
};

/**
 * @param {number} qty
 * @param {string} safeItemKey
 * @param {boolean} [isRetry]
 * @returns {Promise<void>}
 */
const internal_changeLineItemQty = async (
  qty,
  safeItemKey,
  isRetry = false
) => {
  const realKey = await findRealCartKeyBySafeKey(safeItemKey, isRetry);
  const res = await apiRequestRaw("POST", "/cart/change.js?z=1", {
    id: realKey,
    quantity: qty,
  });

  log("API-INTERNAL", "internal__changeLineItem", "yellow", res);

  await res.json();

  if (res.status == 400) {
    log("ERROR RETRY", "Bad request error", "orange");
    await internal_changeLineItemQty(qty, safeItemKey, true);
  }
};

/**
 * This attemps to add variant to cart but silently fails
 * an failed to update. Used in a retry safeguard.
 *
 * @param {number} qty
 * @param {string} safeItemKey
 * @returns {Promise<void>}
 */
const attemptAddtoCartWithSafeguard = async (qty, safeItemKey) => {
  /** @type {Record<string, number>} */
  const body = { variantId: qty };

  try {
    await addToCartRaw(body);
  } catch (err) {
    console.error(
      `[ERROR UPDATE-QTY=${qty}] Retry as insert failed: ${safeItemKey}`,
      err
    );
  }
};

/**
 * This will change the line quantity for a line item.
 * It will handle errors gracefully so calling code can assume it was successful so we don't
 * block the cart. This works fine for qty=0 as it will most likely mean it has already been
 * removed (maybe in another tab).
 *
 *  If qty > 0 we don't really want that so here it will assume if doesn't exist then we perform
 *  an update instead of failing the change qty.
 *
 * @param {number} qty
 * @param {string} safeItemKey
 * @returns {Promise<void>}
 */
const changeLineItemQty = async (qty, safeItemKey) => {
  try {
    return await internal_changeLineItemQty(qty, safeItemKey);
  } catch (err) {
    if (err instanceof CustomShopifyAPIError) {
      // If it failed but we have more than 0 we can just treat that as an insert
      if (qty > 0) {
        await attemptAddtoCartWithSafeguard(qty, safeItemKey);
        return;
      }

      log();
      return;
    }
    throw err;
  }
};

/**
 * @param {LineItemUpdateBody[]} updates
 */
const updateLineItems = async (updates) => {
  /**
   * @type {Record<string, number>}
   */
  const body = {};

  for (const update of updates) {
    const realKey = await findRealCartKeyBySafeKey(update.safeItemKey);
    await getCart();
    body[realKey] = update.qty;
  }

  await apiRequest("POST", "/cart/update.js", { updates: body });
};

/**
 * @param {string} name
 * @param {string} value
 * @returns {Promise<any>}
 */
const setCartAttribute = async (name, value) => {
  return await apiRequest("POST", "/cart/update.js", {
    attributes: {
      [name]: value,
    },
  });
};

/**
 * @param {string} safeItemKey;
 */
const removeFromCart = async (safeItemKey) =>
  await changeLineItemQty(0, safeItemKey);

// TODO: Remove a all but add and update
var API = {
  addToCartRaw,
  setCartAttribute,
  getHTML,
  addToCart,
  getCart,
  updateLineItems,
  changeLineItemQty,
  removeFromCart,
  updateCartNote,
};

// TESTING
let DEBUG_TRACKING = false;

try {
  if (localStorage.getItem("NUUN_SEGMENT_DEBUG") === "true") {
    DEBUG_TRACKING = true;
  }
} catch (err) {
  console.log("Failed to get item from local storage", err);
}

if (DEBUG_TRACKING) {
  console.log("%c=====================", "color:lime;");
  console.log("%cSegment debugging enabled", "color:lime;");
  console.log("%c=====================", "color:lime;");
}

const NUUN_UTM_KEY = "nuun_utm";

// const SERVICE_BASE_ENDPOINT = "http://localhost:3000";
const SERVICE_BASE_ENDPOINT = "https://nestle-forms-a6dcf75f16cb.herokuapp.com";

const currency = window.Shopify.currency.active;

const allowedParamsMap = {
  utm_source: true,
  utm_medium: true,
  utm_campaign: true,
  utm_term: true,
  utm_content: true,
};

const segment = () => {
  if (DEBUG_TRACKING) {
    return {
      track: function (name, params) {
        console.log("%c[SEGMENT TRACK] %s", "color:lime;", name, params);
      },
      identify: function (id, params) {
        console.log("%c[SEGMENT IDENTIFY] %s", "color:lime;", id, params);
      },
    };
  }

  return (
    window.analytics || { track: function () {}, identify: function () {} }
  );
};

const str = (value) =>
  value === undefined || value === null ? "" : value.toString();

const fullImageURL = (imagePath) => {
  return imagePath ? `https:${imagePath}` : "";
};

const getFirstOptionName = (product) => {
  const firstOption = product.options[0];

  if (!firstOption) {
    return "";
  }

  if (typeof firstOption === "string") {
    return firstOption;
  }

  return firstOption.name;
};

const isFlavorProduct = (product) => getFirstOptionName(product) === "Flavor";
const isSizeProduct = (product) => getFirstOptionName(product) === "Size";

const getStoredUTMParams = () => {
  const url = localStorage.getItem(NUUN_UTM_KEY);
  return (url ? url : "").trim();
};

const destroyLocalTracking = () => {
  localStorage.removeItem(NUUN_UTM_KEY);
};

const storeUTM = () => {
  const utmParams = [];
  const url = new URL(window.location.href);

  for (const param of url.searchParams) {
    if (allowedParamsMap[param[0]]) {
      utmParams.push(param[0] + "=" + param[1]);
    }
  }

  if (utmParams.length > 0) {
    localStorage.setItem(NUUN_UTM_KEY, utmParams.join("&"));
    API.updateCartNote(utmParams.join("&"));
  }
};

const trackCartEventInternal = (eventName) => {
  try {
    // @ts-ignore
    const cartJSON = document.querySelector("#cart-data").innerText;
    const cart = JSON.parse(cartJSON);

    if (!cart) {
      console.error('No cart object for event "%s", eventName');
      return;
    }

    const items = cart.items.map((item) => {
      let flavor = "";
      let size = "";

      if (item.options_with_values.length > 0) {
        if (item.options_with_values[0].name === "Flavor") {
          flavor = item.variant_title;
        } else if (item.options_with_values[0].name === "Size") {
          size = item.variant_title;
        }
      }

      let isRecurring = false;
      let frequency = "";

      if (item.selling_plan) {
        const plan = item.selling_plan.name;
        isRecurring = true;
        frequency = "1";

        if (plan.name === "Delivery every 2 Months") {
          frequency = "2";
        } else if (plan.name === "Delivery every 3 Months") {
          frequency = "3";
        }
      }

      return {
        flavor: flavor,
        image_url: item.image,
        name: item.product_title,
        price: item.price / 100.0,
        product_id: str(item.product_id),
        quantity: item.quantity,
        recurring: isRecurring,
        recurring_frequency: frequency,
        size: size,
        sku: item.sku,
        total_price: item.line_price / 100.0,
        url: item.url,
      };
    });

    segment().track(eventName, {
      category: "",
      currency: currency,
      revenue: cart.total_price / 100.0,
      products: items,
    });
  } catch (e) {
    console.error(`Error tracking cart event: ${eventName}`, e);
  }
};

const trackCartViewed = () => {
  trackCartEventInternal("Cart Viewed");
};

const trackCheckoutStarted = () => {
  trackCartEventInternal("Checkout Started");
};

const generateSegmentUID = async (email) => {
  const body = { email: email };
  const appKey = document.querySelector("body").dataset.app;

  if (!appKey) {
    console.log("No app key for UID app found!");
  }

  const url = `${SERVICE_BASE_ENDPOINT}/${appKey}/uid`;
  const res = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return await res.json();
};

const identifyCustomer = async () => {
  if (!window.ShopifyCustomerId) {
    return;
  }

  let requireIdentify = false;

  // Hacky, but can't really track signin or register accurately.
  if (window.location.pathname.indexOf("/account/login") !== 0) {
    const formType = localStorage.getItem("SHOPIFY_FORM_SUBMIT");

    if (formType && window.ShopifyCustomerId) {
      if (formType === "LOGIN_FORM") {
        trackSignedIn(window.ShopifyCustomerEmail);
        requireIdentify = true;
      } else if (formType === "REGISTER_FORM") {
        trackSignedIn(window.ShopifyCustomerEmail);
        requireIdentify = true;
      }
    }

    if (requireIdentify) {
      await rawIdentifyCustomer(
        window.ShopifyCustomerEmail,
        window.ShopifyCustomerName
      );
    }

    localStorage.removeItem("SHOPIFY_FORM_SUBMIT");
  }
};

const rawIdentifyCustomer = async (email, name, extraAttributes) => {
  try {
    const data = await generateSegmentUID(email);

    segment().identify(data.uid, {
      email: email,
      name: name,
      ...extraAttributes,
    });
  } catch (err) {
    console.error("ERROR SEGMENT IDENTIFY", err);
  }
};

const trackQuestionStarted = (product) => {
  segment().track("Question Started", {
    product_name: product.title,
    ux_source: "PDP",
  });
};

const trackReviewStarted = (product) => {
  segment().track("Review Started", {
    product_name: product.title,
    ux_source: "PDP",
  });
};

const trackSignedIn = (email) => {
  segment().track("Signed In", { email });
};

/**
 * @param {string} eventName
 * @param {any} quantity
 * @param {any} variantId
 * @param {any} productData
 * @param {any} trackingCategory
 * @param {any} trackingSource
 * @param {boolean} isRecurring
 * @param {any} [frequency]
 */
const trackCartItemEventInternal = (
  eventName,
  quantity,
  variantId,
  productData,
  trackingCategory,
  trackingSource,
  isRecurring = false,
  frequency = null
) => {
  try {
    const variantIdSafe = parseInt(variantId);
    const variant = productData.variants.find((x) => x.id === variantIdSafe);

    if (!variant) {
      console.error(
        "[NO VARIANT] Missing variant in productData for add to cart event"
      );
      return;
    }

    let flavor = "";
    let size = "";

    if (isFlavorProduct(productData)) {
      flavor = variant.title;
    } else if (isSizeProduct(productData)) {
      size = variant.title;
    }

    const url = `https://${window.location.hostname}/products/${productData.handle}?variant=${variant.id}`;

    segment().track(eventName, {
      brand: "Nuun",
      cart_id: "",
      category: trackingCategory,
      event_source: "",
      flavor: flavor,
      label: productData.title,
      name: productData.title,
      price: variant.price / 100.0,
      product_category: productData.type,
      product_id: str(productData.id),
      quantity: quantity,
      recurring: isRecurring,
      recurring_frequency: frequency ? frequency.toString() : "",
      size: size,
      sku: variant.sku,
      total_price: (quantity * variant.price) / 100.0,
      url: url,
      ux_source: trackingSource,
    });
  } catch (e) {
    console.error(`Error tracking cart item event: ${eventName}`, e);
  }
};

/**
 * @param {number} quantity
 * @param {number} variantId
 * @param {import("../types/products").Product} productData
 * @param {string} trackingCategory
 * @param {string} trackingSource
 * @param {boolean} isRecurring
 * @param {string|null} [frequency]
 */
const trackMikMakClicked = (
  quantity,
  variantId,
  productData,
  trackingCategory,
  trackingSource,
  isRecurring = false,
  frequency = null
) => {
  trackCartItemEventInternal(
    "StoreLocator Modal Viewed",
    quantity,
    variantId,
    productData,
    trackingCategory,
    trackingSource,
    isRecurring,
    frequency
  );
};
/**
 * @param {number} quantity
 * @param {number} variantId
 * @param {import("../types/products").Product} productData
 * @param {string|null} trackingCategory
 * @param {string|null} trackingSource
 * @param {boolean} isRecurring
 * @param {string|null} frequency
 */
const trackAddToCart = (
  quantity,
  variantId,
  productData,
  trackingCategory,
  trackingSource,
  isRecurring,
  frequency
) => {
  trackCartItemEventInternal(
    "Product Added",
    quantity,
    variantId,
    productData,
    trackingCategory,
    trackingSource,
    isRecurring,
    frequency
  );
};

const trackFlavorFilterClicked = (productName, flavor) => {
  segment().track("Flavor Filter Clicked", {
    category: "pdp",
    flavor,
    label: productName,
  });
};

const trackSizeFilterClicked = (productName, size) => {
  segment().track("Size Filter Clicked", {
    category: "pdp",
    size,
    label: productName,
  });
};

const trackPackageFilterClicked = (productName, size) => {
  segment().track("Product Package Filter Clicked", {
    category: "pdp",
    size,
    label: productName,
  });
};

const trackListPageViewed = (collectionName) => {
  segment().track("Product List Page Viewed", {
    brand: "Nuun",
    category: "product_list_page",
    label: "page_viewed",
    list_id: collectionName,
    products: [],
  });
};

const trackNewsletterSubscribed = (email, buttonText, category) => {
  segment().track("Newsletter Subscribed", {
    category: category,
    email: email,
    label: buttonText,
  });
};

/**
 * @param {string} eventName
 * @param {import("../types/products").Product} productData
 * @param {number} variantId
 * @param {string|null} trackingCategory
 * @param {string|null} trackingSource
 */
const trackVariantEvent = (
  eventName,
  productData,
  variantId,
  trackingCategory,
  trackingSource
) => {
  const variant = productData.variants.find((x) => x.id === variantId);
  if (!variant) {
    console.log("trackVariantEvent: variant not found", productData, variantId);
    return;
  }

  const url = `https://${window.location.hostname}/products/${productData.handle}?variant=${variant.id}`;

  let flavor = "";
  let size = "";

  if (isFlavorProduct(productData)) {
    flavor = variant.title;
  } else if (isSizeProduct(productData)) {
    size = variant.title;
  }

  /** @type {any} */ const data = {
    brand: "Nuun",
    flavor: flavor,
    label: productData.title,
    image_url: fullImageURL(productData.featured_image),
    name: productData.title,
    price: variant.price / 100.0,
    product_category: productData.type,
    product_id: str(productData.id),
    quantity: 1,
    size: size,
    sku: variant.sku,
    total_price: variant.price / 100.0,
    url: url,
  };

  if (trackingCategory) {
    data.category = trackingCategory;
  }

  if (trackingSource) {
    data.ux_source = trackingSource;
  }

  segment().track(eventName, data);

  if (!variant.available) {
    trackProductOutOfStockViewed(productData, variant);
  }
};

/**
 * @param {import("../types/products").Product} productData
 * @param {number} variantId
 * @param {string|null} trackingCategory
 * @param {string|null} trackingSource
 */
const trackProductViewed = (
  productData,
  variantId,
  trackingCategory,
  trackingSource
) => {
  trackVariantEvent(
    "Product Viewed",
    productData,
    variantId,
    trackingCategory,
    trackingSource
  );
};

/**
 * @param {import("../types/products").Product} productData
 * @param {number} variantId
 * @param {string|null} trackingCategory
 * @param {string|null} trackingSource
 */
const trackProductCardViewed = (
  productData,
  variantId,
  trackingCategory,
  trackingSource
) => {
  trackVariantEvent(
    "Product Card Viewed",
    productData,
    variantId,
    trackingCategory,
    trackingSource
  );
};

/**
 * @param {object} data
 */
function trackCartItemSaveForLater(data) {
  segment().track("Cart Item Saved", data);
}

/**
 * @param {object} data
 */
function trackCartItemQuantityChanged(data) {
  segment().track("Cart Item Quantity Changed", data);
}

/**
 * @param {object} data
 */

function trackCartItemRemoved(data) {
  segment().track("Cart Item Removed", data);
}

const trackSignupStarted = () => {
  segment().track("Sign Up Started", {});
};

const trackProductOutOfStockViewed = (productData, variant) => {
  let flavor = "";
  let size = "";

  if (isFlavorProduct(productData)) {
    flavor = variant.title;
  } else if (isSizeProduct(productData)) {
    size = variant.title;
  }
  segment().track("Product Out Of Stock Viewed", {
    category: productData.title,
    flavor: flavor,
    label: variant.sku,
    size: size,
  });
};

const initSectionCTA = () => {
  const ctaLinks = document.querySelectorAll("a[data-tracking-cta='true']");

  for (let i = 0; i < ctaLinks.length; i++) {
    const cta = ctaLinks[i];

    cta.addEventListener("click", (e) => {
      const a = e.currentTarget;
      const category = getAttr(a, "data-tracking-category");

      if (!category) {
        return;
      }

      const source = getAttr(a, "data-tracking-source") || "";
      const section = firstMatchingParent(a, "[data-section-name]");
      const sectionName = section ? section.dataset.sectionName : "";
      const text = a.innerText.trim();

      segment().track("Section CTA Clicked", {
        section_name: sectionName,
        category: category,
        url: a.href,
        text: text,
        source: source,
      });
    });
  }
};

const initPageSpecificEvents = () => {
  if (window.location.pathname.indexOf("/collections/") === 0) {
    const collectionName = window.location.pathname.split("/")[2];
    trackListPageViewed(collectionName);
  }
  initSectionCTA();
};

// Export globally
window.__segmentTrack = function (eventName, data) {
  segment().track(eventName, data);
};
window.__segmentIdentify = function (uuid, data) {
  segment().identify(uuid, data);
};

const msg =
  "Subscriptions are temporarily down while we make some exciting updates! We appreciate your patience. Please check back after March 23, 2022.";

const showAccountManageWarning = () => {
  showAlertDialog(msg);
};

const serializeData = (data) => {
  const formData = [];
  for (let key in data) {
    if (data.hasOwnProperty(key)) {
      const value = data[key];
      formData.push(`${key}=${encodeURIComponent(value)}`);
    }
  }
  return formData.join("&");
};

var initForms = () => {
  change(".form__input", (input) => {
    removeClass(input, "form__input-error");
  });
};

/*
 * Magic. js is just our fancy name for updating content via ajax calls.
 *
 * For an example of this usage see the blog list page.
 *
 * To get it working:
 *   - add a `[data-magic="true"]` attribute to the container element.
 *   - add a `[data-magic-btn="true"]` to an anchor (we will load that url to determine the html)
 *   - add [data-magic-content=replace|update] To either eplace or update the content
 *        - This also must have an id attribute though.
 */

const containerAttr = "data-magic";
const containerSelector$2 = `[${containerAttr}]`;
const contentAttr = "data-magic-content";
const contentSelector = `[${contentAttr}]`;
const btnAttr = "data-magic-btn";

const getChangeInfoFromElement = (elements) => {
  const changes = [];

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    const id = element.id;

    if (!id) {
      console.log(element);
      throw new Error(
        "To use the magic feature your content elements must have an id"
      );
    }
    const type = getAttr(element, contentAttr);
    changes.push({ id: id, type: type });
  }
  return changes;
};

const parseHTML = (html) => {
  const element = document.createElement("html");
  element.innerHTML = html;
  return element;
};

const replaceContent = (sourceElement, destElement) => {
  destElement.replaceChildren(...sourceElement.children);
};

const appendContent = (sourceElement, destElement) => {
  const html = sourceElement.innerHTML;
  destElement.insertAdjacentHTML("beforeend", html);
};

const applyChanges = (changes, html) => {
  const newElement = parseHTML(html);

  for (let i = 0; i < changes.length; i++) {
    const change = changes[i];
    const idSelector = "#" + change.id;
    const existingContainer = document.querySelector(idSelector);
    const newContainer = newElement.querySelector(idSelector);

    if (change.type == "append") {
      appendContent(newContainer, existingContainer);
    } else {
      replaceContent(newContainer, existingContainer);
    }
  }
};

var initMagic = () => {
  // We add the click events on the container, so the events are still
  // active when the content changes.
  click(containerSelector$2, (_doc, e) => {
    if (e.target.matches(`[${btnAttr}]`)) {
      e.preventDefault();
      e.stopPropagation();

      const el = e.target;
      const pageUrl = getAttr(el, "href");

      addClass(el, "is-loading");

      const [container] = getFirstMatchingInfoWithAttr(el, containerAttr);
      const contentElements = container.querySelectorAll(contentSelector);
      const changes = getChangeInfoFromElement(contentElements);

      API.getHTML(pageUrl).then((html) => {
        applyChanges(changes, html);
      });
    }
  });
};

const Splide = window.Splide;
const mdBreakpoint = 768;

// A hack, can't do a min-width for options
const largestScreen = 25000;

const arrowPath =
  "M15.2869 34L29 20L15.2869 6L13 8.33306L24.4295 20L13 31.6669L15.2869 34Z";

const alwaysShowOptions = {
  arrowPath: arrowPath,
  rewind: true,
  perPage: 1,
};

const mobileOnlyOptions = {
  arrowPath: arrowPath,
  rewind: true,
  perPage: 1,
  breakpoints: {
    // Only run slider on "mobile" devices
    [mdBreakpoint]: {
      destroy: false,
    },
    [largestScreen]: {
      destroy: true,
    },
  },
};

const initSliderManually = (
  selectorOrEl,
  sliderOptions = {},
  mountOptions = {},
  transitionOptions = undefined
) => {
  const slider =
    typeof selectorOrEl === "string" ? qs(selectorOrEl) : selectorOrEl;

  let options = hasClass(slider, "is-always")
    ? alwaysShowOptions
    : mobileOnlyOptions;

  //options.video = {
  //  autoplay: true,
  //  mute: true,
  //  loop: true,
  //  hideControls: true,
  //  disableOverlayUI: true,
  //};

  if (hasClass(slider, "is-autoplay")) {
    options = { ...options, autoplay: true };
  }

  if (hasClass(slider, "is-fade")) {
    options = { ...options, type: "fade" };
  }

  options = { ...options, ...sliderOptions };

  const splide = new Splide(slider, options);
  splide.mount(mountOptions, transitionOptions);

  return splide;
};

var initSliders = () => {
  const sliders = document.querySelectorAll(".slider");
  for (let i = 0; i < sliders.length; i++) {
    const slider = sliders[i];

    if (hasClass(slider, "is-manual")) {
      continue;
    }

    initSliderManually(slider);
  }

  triggerResizeEvent();
};

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  pdpVariantChange: "pdp-variant-change"};

/**
 * @type {Object<string, Array<(data: any)=> void>>}
 */
let subscribers = {};

/**
 * @param {string} eventName
 * @param {(data: any) => void} callback
 * @returns {()=>void}
 */
function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

/**
 * @param {string} eventName
 * @param {any} data
 */
function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

const screenLarge = 992;

const isAboveLarge = () => {
  return window.matchMedia(`(min-width: ${screenLarge}px)`).matches;
};

/*
 * Based on Dawn theme's trap focus logic
 */

/**
 * @type {{
 *   focusin: (event: FocusEvent) => void,
 *   focusout: (event: FocusEvent) => void,
 *   keydown: (event: KeyboardEvent) => void
 * }}
 */
const trapFocusHandlers = {
  focusin: () => {},
  focusout: () => {},
  keydown: () => {},
};

/**
 * @param {HTMLElement} element
 */
function isVisible(element) {
  const style = window.getComputedStyle(element);

  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    style.opacity !== "0" &&
    element.offsetParent !== null
  );
}

/**
 * @param {HTMLElement} container
 * @param {boolean} [checkVisibility]
 * @returns {HTMLElement[]}
 */
function getFocusableElements(container, checkVisibility = false) {
  /** @type {HTMLElement[]} */
  const elements = Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );

  return checkVisibility ? elements.filter(isVisible) : elements;
}

/**
 * @param {HTMLElement} container
 * @param {HTMLElement|null} [elementToFocus]
 * @param {boolean} [checkVisibility] = false
 */
function trapFocus(container, elementToFocus, checkVisibility = false) {
  elementToFocus = elementToFocus || container;
  const elements = getFocusableElements(container, checkVisibility);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (/** @type {FocusEvent} */ event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    ) {
      return;
    }

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") {
      return;
    }

    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    // @ts-expect-error
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    // @ts-expect-error
    elementToFocus.value
  ) {
    // @ts-expect-error
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

/**
 * @param {HTMLElement|null} elementToFocus
 */
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) {
    elementToFocus.focus();
  }
}

const getFlavorNameWithoutSize = (variantName) => {
  const info = getFlavorFilterInfo(variantName);
  return info.flavor;
};

/**
 * @param {string} variantName
 */
const getFlavorFilterInfo = (variantName) => {
  const info = {
    caffeine: false,
    size: "normal",
    flavor: variantName,
  };

  if (variantName.indexOf("Caffeine") !== -1) {
    info.caffeine = true;
  }

  if (variantName.indexOf("12 Sachets") !== -1) {
    info.flavor = variantName.replace(" (12 Sachets)", "");
    info.size = "sachets12";
  } else if (variantName.indexOf("16 Sachets") !== -1) {
    info.flavor = variantName.replace(" (16 Sachets)", "");
    info.size = "sachets16";
  } else if (variantName.indexOf("20 Sachets") !== -1) {
    info.flavor = variantName.replace(" (20 Sachets)", "");
    info.size = "sachets20";
  } else if (variantName.indexOf("10 Tablet Tube") !== -1) {
    info.flavor = variantName.replace(" (10 Tablet)", "");
    info.size = "tube10";
  } else if (variantName.indexOf("8 Tube Box") !== -1) {
    info.flavor = variantName.replace(" (8 Tube Box)", "");
    info.size = "tubebox8";
  } else if (variantName.indexOf("8-ct") !== -1) {
    info.flavor = variantName.replace(" 8-ct", "");
    info.size = "box8";
  } else if (variantName.indexOf("14-ct") !== -1) {
    info.flavor = variantName.replace(" 14-ct", "");
    info.size = "box14";
  } else if (variantName.indexOf("16-ct") !== -1) {
    info.flavor = variantName.replace(" 16-ct", "");
    info.size = "box16";
  } else if (variantName.indexOf("20-ct") !== -1) {
    info.flavor = variantName.replace(" 20-ct", "");
    info.size = "canister20";
  } else if (variantName.indexOf("Canister") !== -1) {
    info.flavor = variantName.replace(" (Canister)", "");
    info.size = "canister";
  }

  return info;
};

const successIcon =
  '<svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33" fill="none"><path d="M16.4966 1.98047C8.48515 1.98047 1.97656 8.48906 1.97656 16.5005C1.97656 24.5119 8.48515 31.0205 16.4966 31.0205C24.508 31.0205 31.0166 24.5119 31.0166 16.5005C31.0166 8.48906 24.508 1.98047 16.4966 1.98047ZM16.4966 3.30047C23.7939 3.30047 29.6966 9.20318 29.6966 16.5005C29.6966 23.7978 23.7939 29.7005 16.4966 29.7005C9.19927 29.7005 3.29656 23.7978 3.29656 16.5005C3.29656 9.20318 9.19927 3.30047 16.4966 3.30047ZM23.4886 10.8802C23.3159 10.8867 23.1535 10.9614 23.0349 11.0865L14.5169 19.8417L10.7839 16.0055C10.6628 15.8779 10.4952 15.8031 10.3186 15.7992C10.142 15.7954 9.9718 15.8611 9.84548 15.9848C9.71786 16.1073 9.64567 16.2749 9.64438 16.4515C9.64181 16.6281 9.71141 16.7983 9.83516 16.9233L14.0427 21.2443C14.1664 21.3732 14.3379 21.4454 14.517 21.4454C14.6962 21.4454 14.8677 21.3732 14.9914 21.2443L23.9839 12.0043C24.1747 11.8122 24.2288 11.5222 24.1193 11.2734C24.0097 11.0259 23.7593 10.8699 23.4886 10.8802Z" fill="white"/></svg>';

const Toast = {
  addToastListener: function (toaster) {
    toaster.addEventListener("animationend", function (e) {
      if (e.animationName === "hide") {
        return toaster.removeChild(e.target);
      }
    });
  },
  buildToaster: function () {
    var toaster = document.createElement("ul");
    toaster.className = "toast__container";
    Toast.addToastListener(toaster);
    return toaster;
  },
  buildToast: function (type, message) {
    type = typeof type === "undefined" ? "error" : type;
    message = typeof message === "undefined" ? "Message failed" : message;

    // Build elements
    var notice = document.createElement("li"),
      iconHolder = document.createElement("div"),
      content = document.createElement("div");

    iconHolder.innerHTML = successIcon;
    content.innerHTML = message;

    notice.className = "toast toast--" + type;
    notice.appendChild(iconHolder).className = "toast__icon";
    notice.appendChild(content).className = "toast__message";

    return notice;
  },
  addToast: function (type, message) {
    var notice = Toast.buildToast(type, message);
    try {
      document.querySelector("toast__container").appendChild(notice);
    } catch (e) {
      document
        .querySelector("body")
        .appendChild(Toast.buildToaster())
        .appendChild(notice);
    }
  },
};

const successToast = (message) => {
  Toast.addToast("success", message);
};

const REWARD_ID_KEY = "NUUN_REWARD_ID";

const removeItem = (key) => {
  localStorage.removeItem(key);
};

const setItem = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const getItem = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    console.error("ERROR PARSING STORAGE KEY %s", key, e);
    return null;
  }
};

/**
 * @param {string} rewardId
 * @param {string} rewardText
 */
const saveSelectedReward = (rewardId, rewardText) => {
  const data = {
    rewardId,
    rewardText,
  };

  setItem(REWARD_ID_KEY, data);
};

/**
 * @returns {{rewardId: string, rewardText: string}|null}
 */
const getSelectedReward = () => {
  const data = getItem(REWARD_ID_KEY);

  if (data && data.rewardId && data.rewardText) {
    return { rewardId: data.rewardId, rewardText: data.rewardText };
  }
  return null;
};

const removeSelectedReward = () => {
  removeItem(REWARD_ID_KEY);
};

//const url = "http://localhost:2000";
let url = "https://nuun-recharge-connector.herokuapp.com";

const request = async (method, path, body) => {
  const res = await fetch(url + "/loyalty/" + path, {
    method: method,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
  });

  if (res.status !== 200) {
    throw new Error(`Response Error! ${res.statusText}`);
  }
  return await res.json();
};

const fetchRewards = async (customerId) => {
  if (!customerId) {
    let rewards = {
      customerDetails: {
        has_store_account: false,
        points_balance: 0,
      },
      campaigns: [],
    };
    return rewards;
  }

  const data = await request("get", `rewards/${customerId}`);
  return data;
};

const updateBirthday = async (customerId, day, month) => {
  const body = {
    day,
    month,
  };

  const data = await request(
    "post",
    `rewards/${customerId}/update_birthday`,
    body
  );

  return data;
};

const containerSelector$1 = ".custom-dropdown";
const containerOpenSelector = ".custom-dropdown.is-open";

const itemSelector = ".custom-dropdown__ui-content-item";
const selectedTitleSelector = ".custom-dropdown__ui-text";
const openClass$1 = "is-open";

const triggerChangeEvent$1 = (el) => {
  const value = getAttr(el, "data-value");
  const event = new CustomEvent("change", { detail: value });
  el.dispatchEvent(event);
};

const handleKeydownEvent = (e) => {
  if (e.key === "Escape" || e.key === "esc") {
    closeAll();
  }
};

const isOpen = (el) => {
  return hasClass(el, openClass$1);
};

const openUI$1 = (el) => {
  addClass(el, openClass$1);

  const value = getAttr(el, "data-value");
  const items = el.querySelectorAll(itemSelector);

  // Hide the selected option in the dropdown
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (getAttr(item, "data-value") === value) {
      item.style.display = "none";
    } else {
      item.style.display = "";
    }
  }

  document.addEventListener("keydown", handleKeydownEvent);
};

const closeUI$1 = (el) => {
  removeClass(el, openClass$1);
  document.removeEventListener("keydown", handleKeydownEvent);
};

const closeAll = () => {
  removeClass(containerOpenSelector, openClass$1);
};

const selectItem = (el, item) => {
  const value = getAttr(item, "data-value");
  const text = item.innerText;

  const titleEl = qs(selectedTitleSelector, el);

  // Set the title and the data value
  setText(titleEl, text);
  setDataAttr(el, "value", value);

  // Trigger event for any listeners
  triggerChangeEvent$1(el);

  closeUI$1(el);
};

const initCustomDropdown = (el) => {
  el.addEventListener("click", (e) => {
    e.stopPropagation();

    // Selecting an item
    if (e.target.matches(itemSelector)) {
      selectItem(el, e.target);
      return;
    }

    if (isOpen(el)) {
      closeUI$1(el);
    } else {
      openUI$1(el);
    }
  });
};

const getSelectedDropdownValue = (dropdownOrSelector) => {
  const el = qs(dropdownOrSelector);
  return getAttr(el, "data-value");
};

var initDropdowns = () => {
  // If clicking outside of selector force close
  click(document.body, () => {
    closeAll();
  });

  const dropdowns = document.querySelectorAll(containerSelector$1);

  for (let i = 0; i < dropdowns.length; i++) {
    const dd = dropdowns[i];
    initCustomDropdown(dd);
  }
};

var initTabbedMenus = () => {
  // Add animation class
  addClass(".tabbed-menu", "has-entered");

  click(".tabbed-menu__item", (item) => {
    const selectedNum = getAttr(item, "data-menu-num");
    const menu = firstMatchingParent(item, ".tabbed-menu");

    // Sometimes tab content is not a child of the menu, so handle that.
    const externalContainerId = getAttr(menu, "data-menu-external-container");

    // We need to trigger it so any sliders recalculate their sizes
    triggerResizeEvent();

    removeClass(menu, "has-entered");
    setDataAttr(menu, "menu-num-active", selectedNum);

    //<div class="nutrition__images-desktop" data-menu-num-active="1">

    // Set the active number on the external contained if present.
    if (externalContainerId) {
      setDataAttr(externalContainerId, "menu-num-active", selectedNum);
    }

    window.setTimeout(() => {
      addClass(menu, "has-entered");
    }, 100);
  });
};

const selectors = {
  lazyloaded: ".lazyloaded",
};

function removeImageLoadingAnimation(image) {
  // Remove loading animation
  var imageWrapper = image.hasAttribute("data-image-loading-animation")
    ? image
    : image.closest("[data-image-loading-animation]");

  if (imageWrapper) {
    imageWrapper.removeAttribute("data-image-loading-animation");
  }
}

var initImages = () => {
  document.addEventListener("lazyloaded", function (evt) {
    const image = evt.target;

    removeImageLoadingAnimation(image);

    if (image.hasAttribute("data-bgset")) {
      var innerImage = image.querySelector(selectors.lazyloaded);

      if (innerImage) {
        var alt = image.getAttribute("data-alt");
        var src = innerImage.hasAttribute("data-src")
          ? innerImage.getAttribute("data-src")
          : image.getAttribute("data-bg");

        image.setAttribute("alt", alt ? alt : "");
        image.setAttribute("src", src ? src : "");
      }
    }

    if (!image.hasAttribute("data-image")) {
      return;
    }
  });

  // When the theme loads, lazysizes might load images before the "lazyloaded"
  // event listener has been attached. When this happens, the following function
  // hides the loading placeholders.
  function onLoadHideLazysizesAnimation() {
    var alreadyLazyloaded = document.querySelectorAll(".lazyloaded");
    alreadyLazyloaded.forEach(function (image) {
      removeImageLoadingAnimation(image);
    });
  }

  onLoadHideLazysizesAnimation();
};

const widgetsSelector = ".toggle";

const triggerChangeEvent = (el, isSelected) => {
  const event = new CustomEvent("change", { detail: isSelected });
  el.dispatchEvent(event);
};

const setToggleValue = (el, isSelected, triggerChange = true) => {
  const isActive = hasClass(el, "is-active");

  // If no change, no nothing
  if (isActive === isSelected) {
    return;
  }

  if (isActive) {
    removeClass(el, "is-active");
  } else {
    addClass(el, "is-active");
  }

  if (triggerChange) {
    window.setTimeout(() => triggerChangeEvent(el, !isActive));
  }
};

const toggleWidget = (el) => {
  const isActive = hasClass(el, "is-active");
  setToggleValue(el, !isActive);
};

const initWidget = (el) => {
  el.addEventListener("click", (_e) => {
    toggleWidget(el);
  });
};

var initToggleWidget = () => {
  if (window.location.pathname === "/pages/nuun-hydration-quiz") {
    return;
  }

  const widgets = document.querySelectorAll(widgetsSelector);
  for (let i = 0; i < widgets.length; i++) {
    const el = widgets[i];
    initWidget(el);
  }
};

const containerSelector = ".country-ui";
const openClass = "is-open";
const buttonIdSelector = "#country-ui-btn";
const menuIdSelector = "#country-ui-menu";

const handleCloseOnEscape = function (e) {
  switch (e.key) {
    case "Esc":
    case "Escape":
      closeMenu();
      e.stopPropagation();
      e.preventDefault();
      break;
  }
};

const openUI = () => {
  const menu = document.querySelector(menuIdSelector);
  menu.style.display = "block";
  addClass(containerSelector, openClass);
  document.body.addEventListener("keydown", handleCloseOnEscape);
  document.body.addEventListener("click", closeMenu);
};

const closeUI = () => {
  const menu = document.querySelector(menuIdSelector);
  menu.style.display = "none";
  removeClass(containerSelector, openClass);
  document.body.removeEventListener("keydown", handleCloseOnEscape);
  document.body.removeEventListener("click", closeMenu);
};

const closeMenu = () => {
  const btn = document.querySelector(buttonIdSelector);
  closeUI();
  btn.ariaExpanded = false;
  btn.focus();
};

const openMenu = () => {
  const btn = document.querySelector(buttonIdSelector);
  btn.ariaExpanded = true;
  openUI();
};

const toggleMenu = () => {
  const btn = document.querySelector(buttonIdSelector);

  if (btn.ariaExpanded === "true") {
    closeMenu();
  } else {
    openMenu();
  }
};

const initKeyboardEvents = () => {
  const btn = document.querySelector(buttonIdSelector);
  if (!btn) {
    return;
  }
  btn.addEventListener("keydown", (e) => {
    let flag = false;
    const key = e.key;
    switch (key) {
      case " ":
      case "Enter":
        toggleMenu();
        flag = true;
        break;
    }

    if (flag) {
      e.stopPropagation();
      e.preventDefault();
    }
  });
};

var initCountrySelector = () => {
  if (document.querySelector(containerSelector) === null) {
    return;
  }

  click(containerSelector, (el, e) => {
    e.stopPropagation();
    toggleMenu();
  });
  initKeyboardEvents();
};

const COOKIE_NAME = "tracking-preferences";

const getConsentPreferences = () => {
  const data = {
    allowMarketing: true,
  };

  const cookieValue = api.get(COOKIE_NAME);

  if (cookieValue) {
    const prefs = JSON.parse(cookieValue);
    data.allowMarketing = prefs.allowMarketing;
  }

  return data;
};

const setConsentVariablesFromCookie = () => {
  const prefs = getConsentPreferences();
  window.__rmuspc = prefs.allowMarketing ? "YN" : "YY";
};

export { API as $, firstMatchingParent as A, formatShopifyPrice as B, getScriptTagJson as C, isAboveLarge as D, subscribe as E, trackPackageFilterClicked as F, getProductByHandle as G, publish as H, trackProductViewed as I, trackFlavorFilterClicked as J, trackSizeFilterClicked as K, trackMikMakClicked as L, getFlavorFilterInfo as M, showLargeModal as N, getFlavorNameWithoutSize as O, PUB_SUB_EVENTS as P, trapFocus as Q, removeTrapFocus as R, trackQuestionStarted as S, trackReviewStarted as T, trackCartItemRemoved as U, trackCartItemQuantityChanged as V, fetchConfig as W, trackCartViewed as X, showAlertDialog as Y, successToast as Z, trackCartItemSaveForLater as _, addClass as a, getSelectedReward as a0, trackCheckoutStarted as a1, removeSelectedReward as a2, saveSelectedReward as a3, showModalWithContent as a4, isMobile as a5, generateHTMLFromTemplate as a6, replaceChildWithHTML as a7, initCustomDropdown as a8, getSelectedDropdownValue as a9, updateBirthday as aa, fetchRewards as ab, fadeIn as ac, trackAddToCart as ad, trackNewsletterSubscribed as ae, serializeData as af, identifyCustomer as ag, initImages as ah, initTabbedMenus as ai, initSliders as aj, initForms as ak, initToggleWidget as al, initDropdowns as am, initCountrySelector as an, setConsentVariablesFromCookie as ao, storeUTM as ap, initPageSpecificEvents as aq, setDataAttr as b, click as c, destroyLocalTracking as d, getStoredUTMParams as e, removeClass as f, getAttr as g, hasClass as h, setClassWhenTrue as i, initMagic as j, rawIdentifyCustomer as k, initSliderManually as l, triggerResizeEvent as m, fromTodayDuration as n, onReady as o, setText as p, qs as q, runAll as r, showAccountManageWarning as s, trackSignupStarted as t, greetingText as u, appendTextNode as v, pluralize as w, formatPrice as x, trackProductCardViewed as y, qsa as z };
